import { UserType, userTypesMapping, userTypesMappingSimple } from 'constants/userRoles';
import type { HomeConfig, Policy, Role } from 'types';

// roles required to do an action
export const ACTION_ROLES: Policy = {
  analytics: {
    list: ['super-admin', 'admin', 'legal-user', 'reviewer'],
    actions: {
      seeAnalyticsPage: ['litlingo-admin', 'super-admin'],
      sentiment: ['super-admin'],
      downloadCsv: ['super-reviewer', 'legal-user'],
      expandView: ['admin', 'legal-user'],
      editWidget: ['super-admin'],
      tableView: ['admin', 'legal-user'],
      export: ['super-admin'],
    },
  },
  annotators: {
    list: ['super-admin'],
    retrieve: ['super-admin'],
    create: ['super-admin'],
    actions: {
      clone: ['super-admin'],
    },
  },
  assignments: {
    list: ['assignment-admin'],
    retrieve: ['assignment-admin'],
    create: ['assignment-admin'],
    update: ['assignment-admin'],
    delete: ['assignment-admin'],
  },
  auditLogs: {
    list: ['super-admin'],
  },
  global: {
    list: ['super-admin'],
    actions: {
      access: ['super-admin'],
    },
  },
  campaigns: {
    list: ['super-admin'],
    retrieve: ['admin', 'legal-user', 'super-admin'],
    create: ['super-admin'],
    update: ['super-admin'],
    delete: ['super-admin'],
    actions: {
      clone: ['super-admin'],
    },
  },
  categories: {
    list: ['super-admin'],
    retrieve: ['admin', 'legal-user', 'super-admin'],
    create: ['super-admin'],
    update: ['super-admin'],
    delete: ['super-admin'],
    actions: {
      clone: ['super-admin'],
    },
  },
  communications: {
    list: ['super-admin'],
    actions: {
      preview: ['admin', 'legal-user', 'super-admin'],
      visualizeProcessingStatus: ['super-admin'],
      viewCommunicationsAsJson: ['super-admin'],
      reprocess: ['super-admin'],
      export: ['super-reviewer', 'legal-user'],
      advanced: ['super-admin'],
      notify: ['super-reviewer', 'legal-user'],
      seeCommunicationSlug: ['super-admin'],
      sandbox: ['super-admin'],
    },
    protectedFields: {
      audit: {
        unprotected: ['super-admin'],
      },
      recipients: {
        unprotected: ['super-reviewer', 'legal-user'],
      },
      body: {
        unprotected: ['reviewer', 'legal-user'],
      },
      document: {
        unprotected: ['super-admin'],
      },
      entity: {
        unprotected: ['reviewer', 'legal-user'],
      },
    },
  },
  customers: {
    list: ['super-admin', 'system-admin', 'assignment-admin'],
    create: ['super-admin'],
    update: ['super-admin'],
    protectedFields: {
      config: {
        unprotected: ['super-admin'],
      },
    },
    actions: {
      changeCustomer: ['super-reviewer', 'legal-user'],
      reprocessComms: ['super-admin'],
      exportUsers: ['super-reviewer', 'legal-user'],
      showUsers: ['assignment-admin'],
      generateCommunication: ['super-admin'],
      indexHealth: ['super-admin'],
      generalOptions: ['super-admin', 'system-admin', 'assignment-admin'],
      systemOptions: ['super-admin', 'system-admin'],
      advancedOptions: ['super-admin'],
    },
  },
  customReports: {
    list: ['system-admin', 'super-admin'],
    create: ['super-admin'],
    update: ['super-admin'],
  },
  entities: {
    list: ['super-admin'],
    create: ['super-admin'],
    update: ['super-admin'],
  },
  notifications: {
    list: ['super-admin', 'system-admin', 'assignment-admin', 'legal-user'],
    create: ['super-admin'],
    update: ['super-admin'],
  },
  events: {
    list: ['reviewer', 'super-reviewer', 'legal-user'],
    protectedFields: {
      created_by: {
        unprotected: ['super-admin'],
      },
      recipients: {
        unprotected: ['super-admin'],
      },
      hideLabel: {
        unprotected: ['super-admin'],
      },
      fullMessage: {
        unprotected: ['super-admin'],
      },
    },
    actions: {
      viewExplore: ['super-reviewer', 'legal-user', 'reviewer'],
      visualizeLanguage: ['super-admin'],
      viewEventAsJson: ['super-admin'],
      viewLogs: ['reviewer', 'super-reviewer', 'legal-user'],
      reprocess: ['super-admin'],
      createLabel: ['super-admin'],
      broadSearch: ['reviewer', 'legal-user'],
      switchViews: ['super-admin'],
      seeFilterPills: ['super-admin'],
    },
  },
  experiments: {
    list: ['super-admin'],
    retrieve: ['super-admin'],
    create: ['super-admin'],
    update: ['super-admin'],
    delete: ['super-admin'],
  },
  models: {
    list: ['super-admin'],
    retrieve: ['admin', 'legal-user', 'super-admin'],
    create: ['super-admin'],
    update: ['super-admin'],
    delete: ['super-admin'],
    actions: {
      clone: ['super-admin'],
    },
  },
  models_metrics: {
    list: ['super-admin'],
    retrieve: ['super-admin'],
    create: ['super-admin'],
    update: ['super-admin'],
    delete: ['super-admin'],
  },
  outcomes: {
    list: ['super-admin'],
    retrieve: ['super-admin'],
  },
  prompts: {
    list: ['super-admin'],
    retrieve: ['super-admin'],
  },
  tasks: {
    list: ['super-admin'],
    retrieve: ['super-admin'],
    create: ['super-admin'],
    update: ['super-admin'],
    delete: ['super-admin'],
  },

  reviewSets: {
    list: ['admin', 'legal-user', 'reviewer'],
    retrieve: ['assignment-admin'],
    create: ['assignment-admin'],
    update: ['assignment-admin'],
    delete: ['assignment-admin'],
    actions: {
      configure: ['assignment-admin'],
      redesign: ['super-admin'],
      reviewManager: ['assignment-admin', 'super-admin'],
      edit: ['super-admin'],
      appendEnvelope: ['super-admin'],
      needToUpdateWarning: ['super-admin'],
      tenancyField: ['super-admin'],
    },
  },
  rules: {
    list: ['super-admin'],
    retrieve: ['super-admin'],
    create: ['super-admin'],
    delete: ['super-admin'],
    actions: {
      compare: ['super-admin'],
    },
  },
  tags: {
    list: ['system-admin', 'super-admin'],
    retrieve: ['system-admin', 'super-admin'],
    create: ['super-admin'],
    actions: {
      addAnyTag: ['reviewer', 'legal-user'],
      seeTenancy: ['super-admin'],
    },
  },
  test_cases: {
    list: ['test-admin', 'super-admin', 'admin'],
    retrieve: ['test-admin', 'super-admin'],
    create: ['super-admin'],
    actions: {
      createFromTestPage: ['super-admin', 'test-admin'],
      chooseEntity: ['super-admin'],
      viewMetrics: ['test-admin'],
      tags: ['super-admin'],
      askAiToGenerate: ['super-admin'],
      v2Graph: ['super-admin'],
      customerTestPage: [
        'assignment-admin',
        'super-reviewer',
        'admin',
        'super-admin',
        'legal-user',
      ],
    },
  },
  teams: {
    list: ['super-admin', 'system-admin'],
    retrieve: ['super-admin'],
    create: ['super-admin'],
  },
  templates: {
    list: ['super-admin'],
    retrieve: ['super-admin'],
    create: ['super-admin'],
  },
  users: {
    list: ['reviewer', 'legal-user'],
    create: ['super-admin', 'role-admin'],
    actions: {
      viewUsersPage: ['admin', 'legal-user', 'role-admin', 'system-admin'],
      viewUsersPermissionsPage: ['admin', 'legal-user', 'role-admin', 'system-admin'],
      me: ['super-admin'],
      analytics: ['super-admin', 'analytics-user'],
      advanced: ['super-admin', 'system-admin'],
      editAdvanced: ['super-admin'],
      domains: ['super-admin'],
      trueUsers: ['super-admin'],
    },
  },
  identifiers: {
    list: ['super-admin'],
    retrieve: ['super-admin'],
    create: ['super-admin'],
    delete: ['super-admin'],
    actions: {
      clone: ['super-admin'],
    },
  },
  identities: {
    list: ['super-admin'],
    retrieve: ['super-admin'],
    create: ['super-admin'],
    delete: ['super-admin'],
    actions: {
      spoofUsers: ['super-admin'],
    },
  },
  invitations: {
    list: ['super-admin'],
    retrieve: ['super-admin'],
    create: ['super-admin'],
    delete: ['super-admin'],
  },
  investigations: {
    list: ['investigator'],
    retrieve: ['investigator'],
    create: ['investigator'],
    update: ['investigator'],
    delete: ['investigator'],
    actions: {
      addFromExplore: ['investigator'],
      agentSession: ['super-admin'],
    },
  },
  communication_envelopes: {
    list: ['super-admin', 'super-reviewer', 'legal-user'],
    actions: {
      advanced: ['super-admin'],
      redesign: ['super-admin'],
      filter_label_any_of: ['super-admin'],
      filter_actions: ['super-admin'],
      filter_actions_any_of: ['super-admin'],
      filter_external: ['admin', 'legal-user'],
      filter_same_sentence: ['super-admin'],
      filter_result_of_scan: ['super-admin', 'super-reviewer', 'legal-user'],
      filter_annotators: ['super-admin'],
      create_review_set_modal: ['super-admin'],
      filter_operations: ['super-reviewer', 'legal-user'],
      filter_direction: ['super-reviewer', 'legal-user'],
      filter_search_advanced: ['super-admin'],
      filter_categories: ['reviewer', 'legal-user'],
      filter_workflow: ['super-reviewer', 'legal-user'],
      filter_division: ['super-reviewer', 'legal-user'],
      filter_status: ['reviewer', 'legal-user'],
      filter_confirmed: ['super-reviewer', 'legal-user'],
      filter_source: ['super-reviewer', 'legal-user'],
      filter_has_attachment_hits: ['super-admin'],
      filter_misc_actions: ['super-reviewer', 'legal-user'],
      more_filters: ['super-reviewer', 'legal-user'],
      add_tags: ['reviewer', 'legal-user'],
      more_like_this: ['super-admin'],
      export_single_envelope: ['super-reviewer', 'legal-user'],
      go_to_clone: ['super-admin'],
      skipped_flow: ['super-admin'],
      model_transparency: ['super-admin'],
    },
  },
  actions: {},
  custom_reports: {
    list: ['super-admin'],
    retrieve: ['super-admin'],
    create: ['super-admin'],
    delete: ['super-admin'],
  },
};

export const USER_HOME: HomeConfig = {
  'super-admin': { routeName: 'dashboard' },
  'system-admin': { routeName: 'customer' },
  'legal-user': { routeName: 'review-set' },
  'not-authorized': { routeName: 'not-authorized' },
  default: { routeName: 'dashboard' },
};

export const userRolesToTypes = (roles: Role[], useSimpleRoles = false): UserType[] => {
  const userTypes: UserType[] = [];
  const _userTypesMapping = useSimpleRoles ? userTypesMappingSimple : userTypesMapping;
  Object.entries(_userTypesMapping).forEach(([, value]) => {
    if (value.roles.every((r) => roles.includes(r))) {
      userTypes.push(value);
    }
  });

  return userTypes;
};

export const userTypesToRoles = (
  userTypes: UserType['label'][],
  useSimpleRoles = false
): Role[] => {
  const userRoles: Role[] = [];
  const _userTypesMapping = useSimpleRoles ? userTypesMappingSimple : userTypesMapping;

  userTypes.forEach((type) => {
    const normalizedType = type.toLowerCase().trim();
    const userType = Object.values(_userTypesMapping).find(
      (uT) => uT.label.toLowerCase().trim() === normalizedType
    );
    if (userType) {
      userRoles.push(...userType.roles);
    }
  });

  return [...new Set(userRoles)];
};
